import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
} from "react";

// Define the possible screen sizes
export type ScreenSize = "mobile" | "tablet" | "desktop";

interface ScreenSizeContextProps {
  screenSize: ScreenSize;
}

// Create the context
const ScreenSizeContext = createContext<ScreenSizeContextProps | undefined>(
  undefined
);

interface ScreenSizeProviderProps {
  children: ReactNode;
}

// Helper function to get initial screen size
const getInitialScreenSize = (): ScreenSize => {
  if (typeof window !== "undefined") {
    const width = window.innerWidth;
    if (width < 600) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  }
  return "desktop";
};

// Create a provider component
export const ScreenSizeProvider: FC<ScreenSizeProviderProps> = ({
  children,
}) => {
  const [screenSize, setScreenSize] = useState<ScreenSize>(
    getInitialScreenSize()
  );

  useEffect(() => {
    const determineScreenSize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    window.addEventListener("resize", determineScreenSize);
    return () => window.removeEventListener("resize", determineScreenSize);
  }, []);

  return (
    <ScreenSizeContext.Provider value={{ screenSize }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

// Custom hook to access the context
export const useScreenSize = (): ScreenSizeContextProps => {
  const context = useContext(ScreenSizeContext);
  if (context === undefined) {
    throw new Error("useScreenSize must be used within a ScreenSizeProvider");
  }
  return context;
};
